import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StudentPicker } from "@/components/StudentPicker";
import { ReactionThread } from "@/components/ReactionThread";
import { toast } from "sonner";

const STATUS = ["Hadir", "Izin", "Sakit", "Alpha"];
const AKHLAK = ["Kurang", "Cukup", "Baik", "Sangat Baik"];

export default function InputLaporan() {
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState(null);
  const [q, setQ] = useState("");
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({ attendance_status: "Hadir", hafalan_today: "", akhlak: "Baik", note: "" });
  const [saving, setSaving] = useState(false);
  const [evalThread, setEvalThread] = useState([]);
  const [kegThread, setKegThread] = useState([]);
  const [doneIds, setDoneIds] = useState([]);
  const loadDone = () => api.get(`/reports/daily-done?date=${day}`).then((r) => setDoneIds(r.data.ids || [])).catch(() => {});
  const loadThreads = () => {
    if (!sid) return;
    api.get(`/progress?student_id=${sid}&category=evaluasi_harian`).then((r) => setEvalThread((r.data.find((x) => x.item_key === day) || {}).thread || [])).catch(() => {});
    api.get(`/progress?student_id=${sid}&category=kegiatan_harian`).then((r) => setKegThread((r.data.find((x) => x.item_key === day) || {}).thread || [])).catch(() => {});
  };

  useEffect(() => {
    const url = q.trim() ? `/students?search=${encodeURIComponent(q.trim())}` : "/students";
    api.get(url).then((r) => { setStudents(r.data); if (r.data[0] && !q.trim()) setSid((c) => c || r.data[0].id); });
  }, [q]);

  useEffect(() => {
    if (!sid) return;
    api.get(`/reports/daily?student_id=${sid}&date=${day}`).then((r) => {
      const rep = r.data[0];
      if (rep) setForm({ attendance_status: rep.attendance_status, hafalan_today: rep.hafalan_today || "", akhlak: rep.akhlak, note: rep.note || "" });
      else setForm({ attendance_status: "Hadir", hafalan_today: "", akhlak: "Baik", note: "" });
    });
    loadThreads();
  }, [sid, day]);

  useEffect(() => { loadDone(); }, [day]); // eslint-disable-line

  const save = async () => {
    if (!sid) return;
    setSaving(true);
    try {
      await api.post("/reports/daily", { student_id: sid, date: day, ...form });
      toast.success("Laporan harian tersimpan");
      setDoneIds((p) => Array.from(new Set([...p, sid])));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Input Laporan Harian" />
      <div className="px-5 pt-4">
        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Pilih Siswa</p>
        <input data-testid="laporan-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari murid kelas lain (opsional)..."
          className="w-full bg-white border border-stone-200 rounded-2xl py-2.5 px-4 mb-3 outline-none focus:border-primary text-sm" />
        <StudentPicker students={students} value={sid} onChange={setSid} savedIds={doneIds} variant="laporan" />

        {(() => {
          const st = students.find((x) => x.id === sid);
          return st ? (
            <div data-testid="selected-student-info" className="mb-4 bg-primary/5 border border-primary/20 rounded-2xl px-4 py-3">
              <p className="text-[11px] text-stone-400 uppercase tracking-wide mb-0.5">Sedang menginput untuk</p>
              <p className="font-heading font-bold text-primary text-base leading-tight">{st.name}</p>
              <p className="text-xs text-stone-600 mt-1">Kelas: <span className="font-semibold text-stone-700">{st.kelas || "-"}</span></p>
              <p className="text-xs text-stone-600">Umi Guru: <span className="font-semibold text-stone-700">{st.teacher_name || "-"}</span></p>
            </div>
          ) : null;
        })()}

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Tanggal</p>
        <input data-testid="laporan-date-input" type="date" value={day} onChange={(e) => setDay(e.target.value)}
          className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-4 outline-none focus:border-primary" />

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Kehadiran</p>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {STATUS.map((s) => (
            <button key={s} data-testid={`lap-status-${s}`} onClick={() => setForm({ ...form, attendance_status: s })}
              className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${form.attendance_status === s ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-600"}`}>
              {s}
            </button>
          ))}
        </div>

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Hafalan Hari Ini</p>
        <textarea data-testid="lap-hafalan-input" value={form.hafalan_today} onChange={(e) => setForm({ ...form, hafalan_today: e.target.value })} rows={3}
          placeholder="Contoh: Surah Al-Ikhlas ayat 1-4"
          className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-4 outline-none focus:border-primary resize-none" />

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Perilaku / Akhlak</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {AKHLAK.map((a) => (
            <button key={a} data-testid={`lap-akhlak-${a}`} onClick={() => setForm({ ...form, akhlak: a })}
              className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${form.akhlak === a ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-600"}`}>
              {a}
            </button>
          ))}
        </div>

        <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Catatan Guru</p>
        <textarea data-testid="lap-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3}
          placeholder="Catatan aktivitas & perkembangan anak hari ini..."
          className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-4 outline-none focus:border-primary resize-none" />

        {sid && (
          <div className="mb-4 space-y-3" data-testid="guru-parent-threads">
            <div className="bg-white rounded-2xl border border-stone-200/60 p-3 shadow-sm">
              <p className="text-xs font-semibold text-stone-500 mb-1">Komentar Orang Tua — Hafalan Hari Ini</p>
              <ReactionThread studentId={sid} category="evaluasi_harian" itemKey={day} itemLabel="Hafalan Hari Ini" thread={evalThread} onPosted={loadThreads} />
            </div>
            <div className="bg-white rounded-2xl border border-stone-200/60 p-3 shadow-sm">
              <p className="text-xs font-semibold text-stone-500 mb-1">Komentar Orang Tua — Catatan Guru Hari Ini</p>
              <ReactionThread studentId={sid} category="kegiatan_harian" itemKey={day} itemLabel="Catatan Guru Hari Ini" thread={kegThread} onPosted={loadThreads} />
            </div>
          </div>
        )}

        <button data-testid="save-laporan-btn" onClick={save} disabled={saving}
          className="w-full bg-primary text-white font-semibold py-4 rounded-full active:scale-95 transition-transform disabled:opacity-60">
          {saving ? "Menyimpan..." : "Simpan Laporan Harian"}
        </button>
      </div>
    </div>
  );
}
